import type {
  Agreement,
  AgreementStatus,
  AgreementType,
  AmountUnit,
  Payment,
  Profile,
  WalletTransaction,
} from '@/lib/database.types';
import { supabase } from '@/lib/supabase';

export type NewAgreementInput = {
  creatorId: string;
  /** null όταν ο αντισυμβαλλόμενος δεν είναι εγγεγραμμένος (πρόσκληση με email). */
  counterpartyId: string | null;
  counterpartyEmail?: string | null;
  counterpartyName?: string | null;
  title: string;
  description?: string | null;
  amount?: number | null;
  /** Μονάδα ποσού: 'eur' (default) ή 'percent' (μερίδιο). */
  amountUnit?: AmountUnit;
  type: AgreementType;
  expiresAt?: string | null;
  /** Ετικέτα ημερομηνίας (π.χ. 'Λήξη συμφωνίας', 'Παράδοση έργου'). */
  expiresAtLabel?: string | null;
  /** Ημερομηνία έναρξης (για fixed_term). */
  startDate?: string | null;
  /** Ημερομηνία λήξης (για fixed_term). */
  endDate?: string | null;
  /** Πότε μπορεί να σταλεί (cooldown από pair limit). */
  sendAvailableAt?: string | null;
  /** Νόμισμα (default: EUR). */
  currency?: string;
  /** Επισύναψη σελίδας όρων συνεργασίας επαγγελματία στο PDF (default: true). */
  includeProfessionalTerms?: boolean;
};

/** Όλες οι συμφωνίες όπου ο χρήστης είναι μέρος (το RLS φιλτράρει αυτόματα). */
export async function getActiveAgreementPartnerIds(userId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('agreements')
    .select('creator_id, counterparty_id')
    .eq('status', 'active')
    .or(`creator_id.eq.${userId},counterparty_id.eq.${userId}`);
  if (error) throw error;
  const ids = new Set<string>();
  for (const row of data ?? []) {
    if (row.creator_id !== userId && row.creator_id) ids.add(row.creator_id);
    if (row.counterparty_id !== userId && row.counterparty_id) ids.add(row.counterparty_id);
  }
  return ids;
}

export async function listAgreements(): Promise<Agreement[]> {
  const { data, error } = await supabase
    .from('agreements')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export async function getAgreement(id: string): Promise<Agreement | null> {
  const { data, error } = await supabase
    .from('agreements')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function createAgreement(input: NewAgreementInput): Promise<Agreement> {
  const { data, error } = await supabase
    .from('agreements')
    .insert({
      creator_id: input.creatorId,
      counterparty_id: input.counterpartyId,
      counterparty_email: input.counterpartyEmail ?? null,
      counterparty_name: input.counterpartyName ?? null,
      title: input.title,
      description: input.description ?? null,
      amount: input.amount ?? null,
      currency: input.currency ?? 'EUR',
      amount_unit: input.amountUnit ?? 'eur',
      status: 'draft',
      type: input.type,
      expires_at: input.expiresAt ?? null,
      expires_at_label: input.expiresAtLabel ?? null,
      start_date: input.startDate ?? null,
      end_date: input.endDate ?? null,
      send_available_at: input.sendAvailableAt ?? null,
      include_professional_terms: input.includeProfessionalTerms ?? true,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function updateAgreementStatus(
  id: string,
  status: AgreementStatus,
): Promise<Agreement> {
  const { data, error } = await supabase
    .from('agreements')
    .update({ status })
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

/**
 * Μεταφορά draft → pending όταν ο δημιουργός στέλνει τη συμφωνία. Θέτει και
 * creator_agreed: true — η αποστολή ΕΙΝΑΙ η υπογραφή του δημιουργού, αλλιώς
 * η οθόνη συμφωνίας δείχνει λανθασμένα στον ίδιο τον δημιουργό το κουτί
 * "Υπόγραψε/Απόρριψε" που προορίζεται μόνο για τον αντισυμβαλλόμενο.
 *
 * Πρέπει να περάσει από RPC: ο trigger prevent_direct_signing_update
 * απαγορεύει κάθε άμεση εγγραφή στα πεδία υπογραφής από τον client.
 */
export async function sendAgreement(id: string): Promise<Agreement> {
  const { data, error } = await supabase.rpc('send_agreement' as never, {
    p_agreement_id: id,
  } as never);

  if (error) throw error;
  return data as unknown as Agreement;
}

export async function withdrawToDraft(id: string): Promise<void> {
  const { error } = await supabase.rpc('withdraw_to_draft', { p_agreement_id: id });
  if (error) throw new Error(error.message);
}

export async function deleteDraft(id: string): Promise<void> {
  const { error } = await supabase.rpc('delete_draft', { p_agreement_id: id });
  if (error) throw new Error(error.message);
}

/**
 * Αποδοχή ΔΩΡΕΑΝ συμφωνίας απευθείας στη βάση (χωρίς Edge Function/Viva).
 * Θέτει το «agreed» flag του χρήστη· αν έχει ήδη συμφωνήσει το άλλο μέρος,
 * ενεργοποιεί τη συμφωνία (status 'active'). Το RLS επιτρέπει σε μέρος της
 * συμφωνίας να την ενημερώσει.
 */
export async function acceptAgreementFree(
  agreement: Agreement,
  userId: string,
): Promise<Agreement> {
  const isCreator = agreement.creator_id === userId;
  const otherAgreed = isCreator ? agreement.counterparty_agreed : agreement.creator_agreed;

  const now = new Date().toISOString();
  const patch: {
    creator_agreed?: boolean;
    counterparty_agreed?: boolean;
    creator_agreed_at?: string;
    counterparty_agreed_at?: string;
    status?: AgreementStatus;
  } = isCreator
    ? { creator_agreed: true, creator_agreed_at: now }
    : { counterparty_agreed: true, counterparty_agreed_at: now };
  if (otherAgreed) patch.status = 'active';

  const { data, error } = await supabase
    .from('agreements')
    .update(patch)
    .eq('id', agreement.id)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

/** Αίτημα ακύρωσης ενεργής συμφωνίας (με ετικέτες λόγου) → cancel_requested. */
export async function requestCancellation(
  agreementId: string,
  userId: string,
  tags: string[],
): Promise<Agreement> {
  const { data, error } = await supabase
    .from('agreements')
    .update({
      status: 'cancel_requested',
      cancel_requested_by: userId,
      cancel_tags: tags,
    })
    .eq('id', agreementId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** Αποδοχή αίτηματος ακύρωσης → αμοιβαία ακύρωση (cancelled_mutual), χωρίς ποινή. */
export async function agreeToCancellation(
  agreementId: string,
  tags: string[],
): Promise<Agreement> {
  const { data, error } = await supabase
    .from('agreements')
    .update({
      status: 'cancelled_mutual',
      cancel_response_tags: tags,
    })
    .eq('id', agreementId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** Άρνηση αίτηματος ακύρωσης → επιστροφή σε active, χωρίς ποινή. */
export async function rejectCancellation(agreementId: string): Promise<Agreement> {
  const { data, error } = await supabase
    .from('agreements')
    .update({
      status: 'active',
      cancel_requested_by: null,
      cancel_tags: [] as string[],
    })
    .eq('id', agreementId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** true αν η συμφωνία θα είναι δωρεάν (έστω ένα μέρος έχει δωρεάν υπόλοιπο). */
export async function agreementIsFree(agreementId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('agreement_is_free', { aid: agreementId });
  if (error) throw error;
  return data ?? true;
}

/** Πόσες δωρεάν συμφωνίες απομένουν στον τρέχοντα χρήστη (0..30). */
export async function myFreeRemaining(): Promise<number> {
  const { data, error } = await supabase.rpc('my_free_remaining');
  if (error) throw error;
  return data ?? 0;
}

/** Πιστώνει το πορτοφόλι του τρέχοντος χρήστη (π.χ. 1€ κατά ακύρωση επί-πληρωμή). */
export async function creditMyWallet(
  amount: number,
  opts?: { reason?: string; agreementId?: string },
): Promise<void> {
  const { error } = await supabase.rpc('credit_my_wallet', {
    amount,
    reason: opts?.reason ?? 'credit',
    aid: opts?.agreementId,
  });
  if (error) throw error;
}

/** Ιστορικό κινήσεων πορτοφολιού του τρέχοντος χρήστη (νεότερες πρώτα). */
export async function listWalletTransactions(): Promise<WalletTransaction[]> {
  const { data, error } = await supabase
    .from('wallet_transactions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/** Αναζήτηση αντισυμβαλλόμενου με email (μέσω ασφαλούς RPC). */
export async function findProfileByEmail(
  email: string,
): Promise<{ id: string; full_name: string | null } | null> {
  const { data, error } = await supabase.rpc('find_profile_by_email', {
    lookup_email: email,
  });

  if (error) throw error;
  return data?.[0] ?? null;
}

/**
 * Φέρνει προφίλ για μια λίστα ids σε ένα query (id → Profile). Περιλαμβάνει
 * phone/signature_svg/signature_text/communication_apps — μόνο για ids που
 * είναι όντως συμβαλλόμενοι σε συμφωνία του καλούντος (χρειάζονται για να
 * ζωγραφιστεί η πραγματική υπογραφή στο PDF / να φανεί το τηλέφωνο στο chat
 * bubble). Μην τη χρησιμοποιείς για τυχαίους/άσχετους χρήστες (π.χ. λίστα
 * κριτικών σε δημόσιο προφίλ) — γι' αυτό υπάρχει το fetchPublicProfilesByIds.
 */
export async function fetchProfilesByIds(ids: string[]): Promise<Record<string, Profile>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return {};

  // get_profiles_public (πρώην profiles_public view) γεμίζει με NULL phone/
  // signature/business_phone/business_email εκτός αν ο καλών όντως μοιράζεται
  // συμφωνία/προσφορά με αυτό το id — που είναι ακριβώς η εγγύηση "όντως
  // συμβαλλόμενοι" που ήδη απαιτεί αυτή η function.
  const { data, error } = await supabase.rpc('get_profiles_public', { p_ids: unique });
  if (error) throw error;

  const map: Record<string, Profile> = {};
  for (const p of (data ?? []) as unknown as Profile[]) map[p.id] = p;
  return map;
}

/**
 * Ίδιο αποτέλεσμα (id → Profile) αλλά ΧΩΡΙΣ ευαίσθητα πεδία (phone,
 * signature_text/svg, communication_apps, wallet_balance κ.λπ.) — για ids που
 * ΔΕΝ έχουν απαραίτητα σχέση με τον καλούντα (π.χ. λίστα όσων άφησαν κριτική
 * σε ένα δημόσιο προφίλ που κοιτάει τυχαίος χρήστης).
 */
export async function fetchPublicProfilesByIds(ids: string[]): Promise<Record<string, Profile>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return {};

  const { data, error } = await supabase.rpc('get_profiles_public', { p_ids: unique });
  if (error) throw error;

  const map: Record<string, Profile> = {};
  for (const p of (data ?? []) as unknown as Profile[]) map[p.id] = p;
  return map;
}

/** Όλες οι πληρωμές του χρήστη για μια συμφωνία (νεότερη πρώτη). */
export async function listPayments(agreementId: string): Promise<Payment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('agreement_id', agreementId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/** Όλες οι πληρωμές του τρέχοντος χρήστη (το RLS φιλτράρει αυτόματα). */
export async function listMyPayments(): Promise<Payment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/** Μία πληρωμή με το id της (ή null). */
export async function getPayment(paymentId: string): Promise<Payment | null> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('id', paymentId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/** Φέρνει συμφωνίες για μια λίστα ids σε ένα query (id → Agreement). */
export async function fetchAgreementsByIds(ids: string[]): Promise<Record<string, Agreement>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return {};

  const { data, error } = await supabase.from('agreements').select('*').in('id', unique);
  if (error) throw error;

  const map: Record<string, Agreement> = {};
  for (const a of data ?? []) map[a.id] = a;
  return map;
}

/**
 * Πλήθος συμφωνιών που περιμένουν τη δική σου ενέργεια — δηλαδή είναι σε
 * `pending` και είσαι ο αντισυμβαλλόμενος (πρέπει να αποδεχτείς/απορρίψεις).
 */
export async function countNeedsAction(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('agreements')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'pending')
    .eq('counterparty_id', userId);

  if (error) throw error;
  return count ?? 0;
}

/** Πλήθος ΕΝΕΡΓΩΝ συμφωνιών όπου ο χρήστης είναι δημιουργός ή αντισυμβαλλόμενος. */
export async function countPendingAgreementsForMe(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('agreements')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'pending')
    .eq('counterparty_id', userId)
    .eq('counterparty_agreed', false);

  if (error) throw error;
  return count ?? 0;
}

export type ActiveCounts = { oneOff: number; ongoing: number; total: number };

export async function countActiveAgreements(userId: string): Promise<ActiveCounts> {
  const { data, error } = await supabase
    .from('agreements')
    .select('type')
    .eq('status', 'active')
    .or(`creator_id.eq.${userId},counterparty_id.eq.${userId}`);

  if (error) throw error;
  const rows = data ?? [];
  const oneOff  = rows.filter((r) => r.type === 'one_off').length;
  const ongoing = rows.filter((r) => r.type === 'ongoing').length;
  return { oneOff, ongoing, total: oneOff + ongoing };
}

export async function requestCompletion(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('request_completion', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

// ── Αίτημα Επεξεργασίας συμφωνίας ────────────────────────────────────────

export async function requestAgreementEdit(agreementId: string, note: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('request_agreement_edit', { p_agreement_id: agreementId, p_note: note });
  if (error) throw new Error(error.message);
}

export async function respondAgreementEditRequest(agreementId: string, approve: boolean): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('respond_agreement_edit_request', { p_agreement_id: agreementId, p_approve: approve });
  if (error) throw new Error(error.message);
}

export type AgreementEditFields = {
  title: string;
  description: string | null;
  amount: number | null;
  currency: string;
  amount_unit: string | null;
  start_date: string | null;
  end_date: string | null;
  expires_at: string | null;
  expires_at_label: string | null;
};

export async function saveAgreementEdit(agreementId: string, fields: AgreementEditFields): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('save_agreement_edit', {
    p_agreement_id: agreementId,
    p_title: fields.title,
    p_description: fields.description,
    p_amount: fields.amount,
    p_currency: fields.currency,
    p_amount_unit: fields.amount_unit,
    p_start_date: fields.start_date,
    p_end_date: fields.end_date,
    p_expires_at: fields.expires_at,
    p_expires_at_label: fields.expires_at_label,
  });
  if (error) throw new Error(error.message);
}

export async function finalizeAgreementEdit(agreementId: string, approve: boolean): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('finalize_agreement_edit', { p_agreement_id: agreementId, p_approve: approve });
  if (error) throw new Error(error.message);
}

// ── Έλεγχοι κατά κατάχρησης ──────────────────────────────────────────────

export async function checkDailyAgreementLimit(userId: string): Promise<{ allowed: boolean; limit: number; used: number }> {
  const { data, error } = await supabase.rpc('check_daily_agreement_limit', { p_user_id: userId });
  if (error) throw new Error(error.message);
  return data as { allowed: boolean; limit: number; used: number };
}

export async function checkPairAgreementLimit(userA: string, userB: string): Promise<{ allowed: boolean; available_at: string | null }> {
  const { data, error } = await supabase.rpc('check_pair_agreement_limit', { user_a: userA, user_b: userB });
  if (error) throw new Error(error.message);
  return data as { allowed: boolean; available_at: string | null };
}

export async function checkCompletionAllowed(agreementId: string): Promise<{ allowed: boolean; can_complete_at: string | null }> {
  const { data, error } = await supabase.rpc('check_completion_allowed', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
  return data as { allowed: boolean; can_complete_at: string | null };
}

export async function confirmCompletion(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('confirm_completion', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

export async function rejectCompletion(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('reject_completion', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

export async function declarePayment(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('declare_payment', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

export async function confirmPayment(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('confirm_payment', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

export async function rejectPayment(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('reject_payment', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

export async function getAgreementStats(): Promise<{
  total: number;
  today: number;
  last_hour: number;
  offers_sent: number;
  users: number;
  disputes_resolved: number;
  professionals: number;
  eur_total: number;
}> {
  const { data, error } = await supabase.rpc('get_agreement_stats');
  if (error) throw new Error(error.message);
  return data as {
    total: number;
    today: number;
    last_hour: number;
    offers_sent: number;
    users: number;
    disputes_resolved: number;
    professionals: number;
    eur_total: number;
  };
}
